import Router from 'next/router';
import { debounce as debounceFn } from 'debounce';
import { makeObservable, action, isObservableObject, observable, reaction, toJS } from 'mobx';
// eslint-disable-next-line import/order
import queryString from 'query-string';
import type { DefaultFilters, LatLngCoordinates } from '@lib/types';

// eslint-disable-next-line import/order
import { sanitizeQueryParam } from '@lib/utils';

import type { FilterStore } from '../FilterStore';

let uiStore: UiStore | null = null;

class UiStore {
  filters: DefaultFilters = {};
  isEmptyFilters = false;
  defaultFilters: DefaultFilters = {};
  reactionDisposer: null | (() => void) = null;
  registeredFilters = false;

  constructor() {
    makeObservable(this, {
      filters: observable,
      syncQueryToFilters: action,
      unregisterFilters: action,
      registerFilters: action,
    });
  }

  syncQueryToFilters = (params?: Record<string, string | undefined>): void => {
    this.isEmptyFilters = true;
    const queryParams = params ?? Router.query;
    const address = sanitizeQueryParam(queryParams.address);
    const center = sanitizeQueryParam(queryParams.center)?.split(',');
    const marker = sanitizeQueryParam(queryParams.marker)?.split(',');
    const zoom = sanitizeQueryParam(queryParams.zoom);

    if (address) {
      this.isEmptyFilters = false;
      this.filters.address = address;
    }

    if (center?.length === 2) {
      this.isEmptyFilters = false;
      this.filters.center = center.map((v) => parseFloat(v)) as LatLngCoordinates;
    }

    if (marker?.length === 2) {
      this.isEmptyFilters = false;
      this.filters.marker = marker.map((v) => parseFloat(v)) as LatLngCoordinates;
    }

    if (zoom) {
      this.isEmptyFilters = false;
      this.filters.zoom = parseFloat(zoom);
    }

    if (this.isEmptyFilters) {
      Object.assign(this.filters, this.defaultFilters);
    }
  };

  handleRouteChangeComplete = (url: string): void => {
    const nextUrlQuery = queryString.parseUrl(url)?.query;
    const nextFiler = nextUrlQuery
      ? ({
          address: nextUrlQuery.address,
          center: Array.isArray(nextUrlQuery.center)
            ? nextUrlQuery.center.join(',')
            : nextUrlQuery.center,
          marker: Array.isArray(nextUrlQuery.marker)
            ? nextUrlQuery.marker.join(',')
            : nextUrlQuery.marker,
          zoom: nextUrlQuery.zoom,
        } as Record<string, string>)
      : {};

    this.syncQueryToFilters(nextFiler);
  };

  unregisterFilters(): void {
    if (this.reactionDisposer) {
      this.registeredFilters = false;
      this.reactionDisposer();
      Router.events.off('routeChangeComplete', this.handleRouteChangeComplete);
    }
  }

  registerFilters(obj: FilterStore, { debounce = 0 } = {}): void {
    if (this.registeredFilters) {
      return;
    }
    this.registeredFilters = true;

    const { filters } = obj;

    if (!isObservableObject(filters)) {
      throw new Error('expects `filters` as observable property');
    }

    this.defaultFilters = toJS(filters);

    Router.events.on('routeChangeComplete', this.handleRouteChangeComplete);

    this.filters = filters;
    this.syncQueryToFilters();

    const cb = debounceFn((nextFilters: DefaultFilters) => {
      const currentQueryParams = queryString.parse(window.location.search);
      const newQueryParams = queryString.stringify({
        ...currentQueryParams,
        ...nextFilters,
        center: nextFilters.center?.join(',') || currentQueryParams.center,
        marker: nextFilters.marker?.join(',') || currentQueryParams.marker,
      });
      const defaultQueryParams = queryString.stringify({
        ...currentQueryParams,
        ...this.defaultFilters,
        center: this.defaultFilters.center?.join(',') || currentQueryParams.center,
        marker: this.defaultFilters.marker?.join(',') || currentQueryParams.marker,
      });

      if (this.isEmptyFilters && newQueryParams === defaultQueryParams) {
        return;
      }

      const nextUrl = `${window.location.pathname}?${newQueryParams}`;
      const prevUrl = `${window.location.pathname}${window.location.search}`;

      if (nextUrl !== prevUrl) {
        Router.replace(window.location.pathname, nextUrl, { shallow: true });
      }
    }, debounce);

    this.reactionDisposer = reaction(() => ({ ...this.filters }), cb);
  }
}

// eslint-disable-next-line import/no-default-export
export default function getUIStore(): UiStore {
  if (uiStore === null) {
    uiStore = new UiStore();
  }

  return uiStore;
}
